import { FC, useMemo } from 'react';
import { useFieldState } from 'informed';
import { useTranslation } from 'react-i18next';
import { capitalize } from '@repo-level/core-front';
import styled from 'styled-components/macro';

import { required, Validator } from 'helpers/validators';
import { Select } from './Select';
import { device } from 'components/styles/MediaQuery';

import { ScError, ScInputStructure } from './structures/styled';

const days = Array.apply(null, Array(31)).map((_, index) => {
  const day = index + 1;
  const dayFull = day > 9 ? `${day}` : `0${day}`;

  return {
    name: dayFull,
    value: dayFull,
  };
});

const months = Array.apply(null, Array(12)).map((_, index) => {
  const month = index + 1;

  return {
    name: month,
    value: (month > 9 ? `${month}` : `0${month}`).toString(),
  };
});

const getYears = () => {
  const currentFullYear = new Date().getFullYear();
  const currentYear = currentFullYear - 18;

  return Array.apply(null, Array(100)).map((_, index) => {
    const year = (Number(currentYear) - index).toString();

    return {
      name: year,
      value: year,
    };
  });
};

const years = getYears();

interface InputDateProps {
  field: string;
  validate?: Validator<any, any>;
  initialValue?: string;
  disabled?: boolean;
}

export const InputDate: FC<InputDateProps> = ({
  field,
  validate,
  initialValue,
  disabled,
}) => {
  const { t } = useTranslation();

  const [initialValueYear, initialValueMonth, initialValueDay] = useMemo(() => {
    if (initialValue == null) return [undefined, undefined, undefined];

    return initialValue.split('-');
  }, [initialValue]);

  const stateDay = useFieldState(`${field}.day`);
  const stateMonth = useFieldState(`${field}.month`);
  const stateYear = useFieldState(`${field}.year`);

  const date = useMemo(() => {
    if (stateDay == null || stateMonth == null || stateYear == null) return;

    if (
      stateDay.value == null ||
      stateMonth.value == null ||
      stateYear.value == null
    ) {
      return;
    }

    return `${stateDay.value}/${stateMonth.value}/${stateYear.value}`;
  }, [stateDay, stateMonth, stateYear]);

  const errorField = useMemo(() => {
    if (validate == null) return;

    if (stateDay == null || stateMonth == null || stateYear == null) return;

    if (
      stateDay.touched == null ||
      stateMonth.touched == null ||
      stateYear.touched == null
    ) {
      return;
    }

    return validate(date, undefined) as string;
  }, [date, stateDay, stateMonth, stateYear, validate]);

  const monthsWithTrans = useMemo(() => {
    return months.map(({ name, ...rest }) => ({
      ...rest,
      name: capitalize(t(`common:month.${name - 1}`)),
    }));
  }, [t]);

  return (
    <ScInputStructure>
      <ScInputDayOfBirthday>
        <Select
          initialValue={initialValueDay}
          field={`${field}.day`}
          animatedPlaceholder={t('glossary:date.dd')}
          options={days}
          validate={required}
          hideErrorMessage
          validateOnBlur
          disabled={disabled}
          withBorderMode
          error={errorField}
        />
        <Select
          initialValue={initialValueMonth}
          field={`${field}.month`}
          animatedPlaceholder={t('glossary:date.mm')}
          options={monthsWithTrans}
          validate={required}
          hideErrorMessage
          validateOnBlur
          disabled={disabled}
          withBorderMode
          error={errorField}
        />
        <Select
          initialValue={initialValueYear}
          field={`${field}.year`}
          animatedPlaceholder={t('glossary:date.yyyy')}
          options={years}
          validate={required}
          hideErrorMessage
          validateOnBlur
          disabled={disabled}
          withBorderMode
          error={errorField}
        />
      </ScInputDayOfBirthday>
      {errorField && <ScError>{t(errorField as string)}</ScError>}
    </ScInputStructure>
  );
};

const ScInputDayOfBirthday = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;

  @media (${device.mobileS}) {
    grid-template-columns: 1fr;
  }
`;
